import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/motion";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  const activeCategory = searchParams.get("category") || "All";
  const rxFilter = searchParams.get("rx"); // "true" | "false" | null

  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    const params = {};
    if (activeCategory !== "All") params.category = activeCategory;
    if (rxFilter === "true") params.rx = true;
    if (rxFilter === "false") params.rx = false;
    if (q) params.q = q;
    const t = setTimeout(() => {
      api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => setLoadError(true)).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [activeCategory, rxFilter, q, retryTick]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value == null) next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const chips = useMemo(() => ["All", ...categories], [categories]);

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-28 pb-10" data-testid="shop-page">
      <Reveal>
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-[#0F5A47] mb-4">Full Catalog</p>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl">The Dispensary</h1>
      </Reveal>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mt-12 mb-10">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="catalog-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search medicines, supplements, devices…"
            className="w-full border hairline bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#0F5A47] transition-colors"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <button
              key={c}
              data-testid={`category-filter-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilter("category", c === "All" ? null : c)}
              className={`px-5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-300 active:scale-95 ${activeCategory === c ? "bg-emerald-800 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200 hover:border-emerald-400"}`}
            >
              {c}
            </button>
          ))}
          <button
            data-testid="rx-filter-toggle"
            onClick={() => setFilter("rx", rxFilter === "true" ? null : "true")}
            className={`px-5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-300 active:scale-95 ${rxFilter === "true" ? "bg-amber-500 text-white shadow-md" : "bg-white text-amber-700 border border-amber-300 hover:bg-amber-50"}`}
          >
            Rx Only
          </button>
        </div>
      </div>

      {loading ? (
        <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400 py-24 text-center" data-testid="catalog-loading">Loading catalog…</p>
      ) : loadError ? (
        <div className="py-24 text-center" data-testid="catalog-error">
          <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">Could not load products — check your connection</p>
          <button onClick={() => setRetryTick((n) => n + 1)} data-testid="catalog-retry-btn" className="mt-4 bg-emerald-800 text-white px-6 py-3 rounded-xl font-bold text-xs hover:bg-emerald-900 transition-colors" >Retry</button>
        </div>
      ) : products.length === 0 ? (
        <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400 py-24 text-center" data-testid="catalog-empty">No products match your filters</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="catalog-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
